/**
 * VariantMatrix - Table showing all variant combinations with accordion expansion
 *
 * Displays variants in a matrix/table format with expandable rows.
 * Each variant row can be expanded to show variant-specific content:
 * - Surface products: Plank images, texture cropper, grout settings
 * - 3D model products: Product image, 3D model upload
 * - All types: Variant links (detail link, add to cart link)
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight,
  CircleArrowUp,
  Crop,
  GripVertical,
  ImageIcon,
  Images,
  Loader2,
  Trash2,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { VariantAxisConfig } from '../../../../../common/models/product-family.model';
import type { VariantV3, VariantGroupV3, AxisValue, VisualizationType } from '../../types';
import { formatAxisValueForDisplay, isColorAxisValue } from '../../types';
import { getCroppedImage } from '../../utils/imageUtils';
import {
  SurfaceExpandedContent,
  Model3DExpandedContent,
  ImageOnlyExpandedContent,
} from './expanded-content';
import { useBuildFromPhotos } from '../shared/media/build-from-photos.store';

// =============================================================================
// Helpers
// =============================================================================

/** Check if a variant has actually changed compared to its original snapshot. */
function isVariantModified(variant: VariantV3): boolean {
  if (variant._isNew) return false;
  if (!variant._originalSnapshot) return false;
  const stripCacheBuster = (url: string | undefined) =>
    url ? url.replace(/[?&]v=\d+/g, '').replace(/\?$/, '') : url;
  const visualizationForSnapshot = variant.visualization ? {
    ...variant.visualization,
    thumbnailPath: undefined,
    surface: variant.visualization.surface ? {
      ...variant.visualization.surface,
      materialPath: stripCacheBuster(variant.visualization.surface.materialPath),
    } : variant.visualization.surface,
  } : variant.visualization;
  const current = JSON.stringify({
    axes: variant.axes,
    sku: variant.sku,
    isSelected: variant.isSelected,
    isDefault: variant.isDefault,
    visualization: visualizationForSnapshot,
    commerce: variant.commerce,
    siblingOrder: variant.siblingOrder,
  }, (_, value) => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const sorted: Record<string, unknown> = {};
      Object.keys(value).sort().forEach(key => { sorted[key] = value[key]; });
      return sorted;
    }
    return value;
  });
  return current !== variant._originalSnapshot;
}

// =============================================================================
// Pattern Translation
// =============================================================================

/**
 * Map pattern values to translation keys
 * Handles various formats: "stackbond", "stack bond", "Stack Bond", etc.
 */
const PATTERN_TRANSLATION_KEYS: Record<string, string> = {
  bricks: 'PATTERNS.BRICKS',
  herringbone: 'PATTERNS.HERRINGBONE',
  chevron: 'PATTERNS.CHEVRON',
  stackbond: 'PATTERNS.STACKBOND',
  opus: 'PATTERNS.OPUS',
  cabochon: 'PATTERNS.CABOCHON',
  hexagonal: 'PATTERNS.HEXAGONAL',
  hexa: 'PATTERNS.HEXAGONAL',
  fishscale: 'PATTERNS.FISHSCALE',
  arabesque: 'PATTERNS.ARABESQUE',
  alure: 'PATTERNS.ALURE',
  arrow: 'PATTERNS.ARROW',
  penny: 'PATTERNS.PENNY',
  triangle: 'PATTERNS.TRIANGLE',
  flagstone: 'PATTERNS.FLAGSTONE',
  flagstonetight: 'PATTERNS.FLAGSTONETIGHT',
  wave: 'PATTERNS.WAVE',
  longhex: 'PATTERNS.LONGHEX',
  alternating: 'PATTERNS.ALTERNATING',
};

/**
 * Get translation key for a pattern value
 */
function getPatternTranslationKey(pattern: string): string | null {
  const normalized = pattern.toLowerCase().replace(/\s+/g, '');
  return PATTERN_TRANSLATION_KEYS[normalized] || null;
}

// =============================================================================
// Types
// =============================================================================

interface VariantMatrixProps {
  /** All variants */
  variants: VariantV3[];
  /** Grouped variants for display */
  variantGroups: VariantGroupV3[];
  /** Enabled axes configuration */
  enabledAxes: VariantAxisConfig[];
  /** Visualization type */
  visualizationType: VisualizationType;
  /** Handler for variant field changes */
  onVariantChange: (uuid: string, field: string, value: unknown) => void;
  /** Handler for variant deletion */
  onDeleteVariant: (uuid: string) => void;
  /** Handler for setting default variant */
  onSetDefault: (uuid: string) => void;
  /** Handler for selecting/deselecting a variant */
  onSelectVariant: (uuid: string, selected: boolean) => void;
  /** Handler for selecting/deselecting all variants */
  onSelectAll: (selected: boolean) => void;
  /** Handler for reordering variants via drag-and-drop */
  onReorderVariants?: (activeId: string, overId: string) => void;

  // Surface product handlers
  /** Uploaded plank images by plankGroupId */
  uploadedPlankImages?: { [plankGroupId: string]: File[] };
  /** Handler for plank images change */
  onPlankImagesChange?: (plankGroupId: string, files: File[]) => void;
  /** Handler to generate material texture */
  onGenerateMaterial?: (variant: VariantV3, plankGroupId: string, files: File[], options?: { randomize?: boolean; mirror?: boolean }) => void;
  /** Set of variant UUIDs currently generating textures */
  generatingVariants?: Set<string>;
  /** Handler for variant zoom change */
  onVariantZoomChange?: (uuid: string, zoom: number) => void;
  /** Handler for variant crop change */
  onVariantCropChange?: (uuid: string, crop: { x: number; y: number }) => void;
  /** Handler for grout size change */
  onVariantGroutSizeChange?: (uuid: string, size: number) => void;
  /** Handler for grout color change */
  onVariantGroutColorChange?: (uuid: string, color: string) => void;
  /** Handler for seamless texture width change (meters) */
  onSeamlessWidthChange?: (uuid: string, width: number) => void;
  /** Handler for seamless texture height change (meters) */
  onSeamlessHeightChange?: (uuid: string, height: number) => void;
  /** Handler for thumbnail change */
  onVariantThumbnailChange?: (uuid: string, thumbnailBase64: string) => void;
  /** Lazy loading callback for plank images */
  onLoadPlankImages?: (plankGroupId: string) => Promise<void>;
  /** Set of plankGroupIds currently loading */
  loadingPlankGroups?: Set<string>;

  // 3D model handlers
  /** Handler for variant image upload (for 3D families) */
  onVariantImageChange?: (uuid: string, imageFile: File) => void;
  /** Handler for 3D model file upload (GLTF/GLB) */
  onVariantModelChange?: (uuid: string, modelFile: File) => void;
  /** The 3D model file picked for a variant but not uploaded yet (sent on save) */
  getPendingModelFile?: (uuid: string) => File | undefined;
  /** Handler for USDZ file upload (iOS AR Quick Look — optional) */
  onVariantUsdzChange?: (uuid: string, usdzFile: File) => void;
  /** Handler to generate 3D model from image */
  onGenerate3DModel?: (uuid: string) => void;
  /** Set of variant UUIDs currently generating 3D models */
  generating3DModels?: Set<string>;

  // Common handlers
  /** Handler for detail link change */
  onVariantLinkChange?: (uuid: string, link: string) => void;
  /** Handler for add to cart link change */
  onVariantAddToCartLinkChange?: (uuid: string, link: string) => void;
}

// =============================================================================
// Helper Components
// =============================================================================

/** Thumbnail with skeleton loading */
const ThumbnailWithSkeleton: React.FC<{ src: string; alt: string }> = ({ src, alt }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <>
      {!imageLoaded && <Skeleton className="absolute inset-0 size-full" />}
      <img
        src={src}
        alt={alt}
        onLoad={() => setImageLoaded(true)}
        className={cn(
          'size-full object-contain transition-opacity duration-300',
          imageLoaded ? 'opacity-100' : 'opacity-0'
        )}
      />
    </>
  );
};

/** One axis value as a pill — rendered per axis in the row and again in the new-default dialog. */
const AxisPill: React.FC<{ value: AxisValue; label: string }> = ({ value, label }) => {
  const colorHex = isColorAxisValue(value) ? value.hex : undefined;

  return (
    <span className="inline-flex items-center gap-1 bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground">
      {colorHex && (
        // The swatch is product data, not a theme colour.
        <span
          className="size-2.5 shrink-0 rounded-full border border-foreground/10"
          style={{ backgroundColor: colorHex }}
        />
      )}
      {label}
    </span>
  );
};

// =============================================================================
// Sortable Row Wrapper
// =============================================================================

/** Wraps a variant's two rows (main + expanded) with useSortable for drag-to-reorder */
const SortableVariantRows: React.FC<{
  id: string;
  disabled?: boolean;
  children: (props: {
    dragHandleProps: Record<string, any>;
    isDragging: boolean;
  }) => React.ReactNode;
}> = ({ id, disabled, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative' as const,
    zIndex: isDragging ? 1 : 'auto',
  };

  return (
    <tbody ref={setNodeRef} style={style} className={cn(isDragging && 'bg-muted')} {...attributes}>
      {children({ dragHandleProps: listeners || {}, isDragging })}
    </tbody>
  );
};

// =============================================================================
// Main Component
// =============================================================================

export const VariantMatrix: React.FC<VariantMatrixProps> = ({
  variants,
  variantGroups,
  enabledAxes,
  visualizationType,
  onVariantChange,
  onDeleteVariant,
  onSetDefault,
  onSelectVariant,
  onSelectAll,
  onReorderVariants,
  uploadedPlankImages = {},
  onPlankImagesChange,
  onGenerateMaterial,
  generatingVariants = new Set(),
  onVariantZoomChange,
  onVariantCropChange,
  onVariantGroutSizeChange,
  onVariantGroutColorChange,
  onSeamlessWidthChange,
  onSeamlessHeightChange,
  onVariantThumbnailChange,
  onLoadPlankImages,
  loadingPlankGroups = new Set(),
  onVariantImageChange,
  onVariantModelChange,
  getPendingModelFile,
  onVariantUsdzChange,
  onGenerate3DModel,
  generating3DModels = new Set(),
  onVariantLinkChange,
  onVariantAddToCartLinkChange,
}) => {
  const { t } = useTranslation();
  const [expandedVariants, setExpandedVariants] = useState<Set<string>>(new Set());
  // Randomize and mirror options for stackbond pattern texture generation,
  // starting from what the product was laid with (e.g. a CSV "stackbond:mirror").
  const storedStackbond = variants.find((v) => typeof v.visualization?.surface?.seamlessParams?.mirror === 'boolean'
    || typeof v.visualization?.surface?.seamlessParams?.randomize === 'boolean')?.visualization?.surface?.seamlessParams;
  const [randomize, setRandomize] = useState(storedStackbond?.randomize ?? true);
  const [mirror, setMirror] = useState(storedStackbond?.mirror ?? false);
  // Upload mode overrides per variant (user can override the auto-detected mode)
  const [variantUploadModes, setVariantUploadModes] = useState<{ [uuid: string]: 'single' | 'multiple' }>({});
  const [showUploadModeInfo, setShowUploadModeInfo] = useState(false);
  // Local state for crop/zoom (for responsive UI)
  const [variantCrops, setVariantCrops] = useState<{ [uuid: string]: { x: number; y: number } }>({});
  const [variantZooms, setVariantZooms] = useState<{ [uuid: string]: number }>({});
  const [variantCroppedAreas, setVariantCroppedAreas] = useState<{ [uuid: string]: { x: number; y: number; width: number; height: number } }>({});
  const [settingThumbnail, setSettingThumbnail] = useState<Set<string>>(new Set());
  // State for prompting new default when deleting the default variant
  const [pendingDefaultDeletion, setPendingDefaultDeletion] = useState<string | null>(null);
  const [selectedNewDefault, setSelectedNewDefault] = useState<string | null>(null);

  // Refs to track latest crop/zoom values (for use in callbacks without stale closures)
  const variantCropsRef = useRef<{ [uuid: string]: { x: number; y: number } }>({});
  const variantZoomsRef = useRef<{ [uuid: string]: number }>({});

  // Debounce timers for auto-saving crop changes
  const cropDebounceTimers = useRef<{ [uuid: string]: NodeJS.Timeout }>({});

  // Track which variant Croppers the user has actually interacted with (drag/scroll).
  // onCropComplete fires on mount too, so we only save when the user has touched the cropper.
  const cropperInteractedRef = useRef<Set<string>>(new Set());

  // Cache blob URLs for File objects to prevent creating a new URL on every render.
  // Without this, URL.createObjectURL(file) in the render loop produces a new blob URL each time,
  // which changes the Cropper's key prop → remount → onCropComplete → state update → infinite loop.
  const blobUrlCacheRef = useRef<Map<File, string>>(new Map());

  const getOrCreateBlobUrl = useCallback((file: File): string => {
    const cached = blobUrlCacheRef.current.get(file);
    if (cached) return cached;
    const url = URL.createObjectURL(file);
    blobUrlCacheRef.current.set(file, url);
    return url;
  }, []);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      blobUrlCacheRef.current.forEach((url) => URL.revokeObjectURL(url));
      blobUrlCacheRef.current.clear();
    };
  }, []);

  // Drag-to-reorder sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id && onReorderVariants) {
      onReorderVariants(active.id as string, over.id as string);
    }
  }, [onReorderVariants]);

  // Calculate selection state
  const selectedCount = variants.filter((v) => v.isSelected).length;
  const allSelected = selectedCount === variants.length && variants.length > 0;
  const someSelected = selectedCount > 0 && selectedCount < variants.length;

  const isSurfaceProduct = visualizationType === 'surface';
  const is3DProduct = visualizationType === 'model3d';

  // Initialize upload mode for surface variants and persist isMultiple on the variant.
  // New variants (no materialPath and no materialPaths) default to "multiple".
  // Existing variants with only a materialPath (seamless texture) stay "single".
  // Existing variants with materialPaths (plank images) stay "multiple".
  useEffect(() => {
    if (!isSurfaceProduct) return;
    const newModes: { [uuid: string]: 'single' | 'multiple' } = {};
    for (const v of variants) {
      if (variantUploadModes[v.uuid]) continue; // already set by user
      if (v.visualization?.surface?.isMultiple != null) {
        // Persisted from a previous session — honour it
        newModes[v.uuid] = v.visualization.surface.isMultiple ? 'multiple' : 'single';
      } else if (!(v.visualization?.surface?.materialPaths?.length) && !v.visualization?.surface?.materialPath) {
        // Brand-new variant with nothing uploaded yet → default to multiple
        newModes[v.uuid] = 'multiple';
      }
    }
    if (Object.keys(newModes).length > 0) {
      setVariantUploadModes(prev => ({ ...prev, ...newModes }));
      // Persist isMultiple and clean up inherited data that doesn't apply to the mode
      for (const [uuid, mode] of Object.entries(newModes)) {
        const v = variants.find(vr => vr.uuid === uuid);
        if (v) {
          const surface = v.visualization?.surface || {};
          const needsUpdate = surface.isMultiple == null
            || (mode === 'single' && (surface.materialPaths?.length || 0) > 0);
          if (needsUpdate) {
            const updatedSurface = { ...surface, isMultiple: mode === 'multiple' };
            if (mode === 'single') {
              // Clear inherited plank data for single-mode variants
              updatedSurface.materialPaths = [];
            }
            onVariantChange(uuid, 'visualization', {
              ...v.visualization,
              surface: updatedSurface,
            });
          }
        }
      }
    }
  }, [variants, isSurfaceProduct, variantUploadModes, onVariantChange]);

  // Display-time stable sort: selected variants first, soft-deleted variants at the bottom.
  // Original relative order is preserved within each group (stable partition).
  // When a variant is restored, it returns to its original position automatically.
  const sortedVariants = useMemo(() => {
    const selected = variants.filter(v => v.isSelected);
    const deselected = variants.filter(v => !v.isSelected);
    return [...selected, ...deselected];
  }, [variants]);

  // Auto-save thumbnail when crop changes (debounced).
  // Debounce is short enough to feel responsive after Generate Seamless (where
  // the cropper fires onCropComplete once on mount and the user expects the
  // row thumbnail to update immediately) but still coalesces rapid slider
  // movements during interactive cropping.
  const autoSaveThumbnail = useCallback((uuid: string, materialUrl: string, croppedAreaPixels: { x: number; y: number; width: number; height: number }) => {
    if (cropDebounceTimers.current[uuid]) {
      clearTimeout(cropDebounceTimers.current[uuid]);
    }

    cropDebounceTimers.current[uuid] = setTimeout(async () => {
      if (!onVariantThumbnailChange) return;

      setSettingThumbnail(prev => new Set(prev).add(uuid));

      try {
        const croppedImage = await getCroppedImage(materialUrl, croppedAreaPixels);
        if (croppedImage) {
          onVariantThumbnailChange(uuid, croppedImage as string);
        }
      } catch (error) {
        console.error('Failed to auto-save thumbnail:', error);
      } finally {
        setSettingThumbnail(prev => {
          const next = new Set(prev);
          next.delete(uuid);
          return next;
        });
      }
    }, 250);
  }, [onVariantThumbnailChange]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(cropDebounceTimers.current).forEach(timer => clearTimeout(timer));
    };
  }, []);

  const toggleVariant = (uuid: string, plankGroupId?: string, isMultipleMode?: boolean) => {
    setExpandedVariants((prev) => {
      const next = new Set(prev);
      if (next.has(uuid)) {
        next.delete(uuid);
        // Reset interaction tracking so next expand doesn't auto-save on mount
        cropperInteractedRef.current.delete(uuid);
      } else {
        next.add(uuid);
        // Lazy load plank images when expanding - only for multiple mode
        // Single mode displays the existing materialPath directly, no loading needed
        if (plankGroupId && onLoadPlankImages && isMultipleMode) {
          onLoadPlankImages(plankGroupId);
        }
      }
      return next;
    });
  };

  // A variant offered photos for Build from photos opens, so its dialog can.
  const photoOffers = useBuildFromPhotos((s) => s.offers);
  useEffect(() => {
    const offered = Object.keys(photoOffers).filter((uuid) => variants.some((v) => v.uuid === uuid));
    if (!offered.length) return;
    setExpandedVariants((prev) => (offered.every((uuid) => prev.has(uuid)) ? prev : new Set([...prev, ...offered])));
  }, [photoOffers, variants]);

  const handleSelectAllClick = () => {
    onSelectAll(!allSelected);
  };

  // Translate axis value for display (especially for patterns)
  const translateAxisValue = useCallback((axisName: string, value: AxisValue): string => {
    const displayValue = formatAxisValueForDisplay(value);

    // Check if this is a pattern axis and translate if possible
    if (axisName === 'pattern' && typeof value === 'string') {
      const translationKey = getPatternTranslationKey(value);
      if (translationKey) {
        return t(translationKey, displayValue);
      }
    }

    return displayValue;
  }, [t]);

  // Get variant display name from axes
  const getVariantDisplayName = (variant: VariantV3): string => {
    const parts: string[] = [];

    enabledAxes.forEach((axis) => {
      const value = variant.axes[axis.name];
      if (value !== null && value !== undefined) {
        parts.push(translateAxisValue(axis.name, value));
      }
    });

    return parts.length > 0 ? parts.join(' / ') : 'Variant';
  };

  // Get plankGroupId from canonical location: visualization.surface.plankGroupId
  const getPlankGroupId = (variant: VariantV3): string => {
    return variant.visualization?.surface?.plankGroupId || variant.uuid;
  };

  /** Tile images (the generator lays them) or a ready-made texture: each mode clears what the other left. */
  const setUploadMode = (variant: VariantV3, mode: 'single' | 'multiple') => {
    setVariantUploadModes(prev => ({ ...prev, [variant.uuid]: mode }));
    const surface = variant.visualization?.surface || {};
    const updatedSurface = mode === 'multiple'
      ? { ...surface, isMultiple: true, materialPath: undefined }
      : { ...surface, isMultiple: false, materialPath: undefined, materialPaths: [] };
    onVariantChange(variant.uuid, 'visualization', {
      ...variant.visualization,
      surface: updatedSurface,
    });
  };

  /** The product's photos (its gallery lives on the default variant), for Build from photos. */
  const productPhotoUrls = (variant: VariantV3): string[] => {
    const owner = variant.galleryImages?.length ? variant : variants.find((v) => v.isDefault);
    return (owner?.galleryImages || []).filter((u) => /^https?:\/\//.test(u));
  };

  // Helper to get proper image URL format
  const getImageSrc = (imageUrl: string | undefined, bustCache: boolean = false): string => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('data:')) return imageUrl;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      if (bustCache) {
        const separator = imageUrl.includes('?') ? '&' : '?';
        return `${imageUrl}${separator}_=${Date.now()}`;
      }
      return imageUrl;
    }
    return `data:image/png;base64,${imageUrl}`;
  };

  // Cells carry no borders of their own — the row draws a hairline box instead.
  const cellClass = 'py-2 align-middle';

  if (variants.length === 0) {
    return (
      <div className="border border-dashed border-border bg-muted/40 p-8 text-center text-xs text-muted-foreground">
        No variants generated yet. Add values to the axes above to create variants.
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div>
        {/* Selection Summary */}
        <div className="mb-2 flex items-center justify-between">
          <Badge variant="outline" className="h-auto px-3 py-1 text-muted-foreground">
            {t('PAGES.NEW_PRODUCT.V3.VARIANTS_SELECTED', { selected: selectedCount, total: variants.length, plural: variants.length !== 1 ? 's' : '' })}
          </Badge>
        </div>

        {/* Table */}
        <table className="w-full border-separate border-spacing-y-1.5 text-xs">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={sortedVariants.filter(v => v.isSelected).map(v => v.uuid)}
              strategy={verticalListSortingStrategy}
            >
            {sortedVariants.map((variant) => {
              const isExpanded = expandedVariants.has(variant.uuid);
              const isGenerating = generatingVariants.has(variant.uuid);
              const plankGroupId = getPlankGroupId(variant);

              const materialUrl = getImageSrc(variant.visualization?.surface?.materialPath);
              const thumbnailUrl = getImageSrc(variant.visualization?.thumbnailPath);
              const modelUrl = variant.visualization?.model3d?.modelPath;
              const usdzUrl = variant.visualization?.model3d?.usdzPath;

              // Determine upload mode: persisted isMultiple > user override > fallback from materialPaths
              const materialPaths = variant.visualization?.surface?.materialPaths || [];
              const defaultMode = variant.visualization?.surface?.isMultiple != null
                ? (variant.visualization.surface.isMultiple ? 'multiple' : 'single')
                : (materialPaths.length > 0 ? 'multiple' : 'single');
              const uploadMode = variantUploadModes[variant.uuid] ?? defaultMode;

              // In single mode, each variant manages its own texture independently —
              // ignore plankGroupId grouping (only relevant for multiple/plank mode).
              const imageGroupKey = uploadMode === 'single' ? variant.uuid : plankGroupId;
              const groupImages = uploadedPlankImages[imageGroupKey] || [];
              // Determine texture preview URL based on upload mode
              // For single mode: use the first uploaded image from groupImages, or existing materialPath/thumbnailPath
              // For multiple mode: use the generated materialPath
              const texturePreviewUrl = (() => {
                if (uploadMode === 'single') {
                  // Single mode: only show the variant's own texture, never inherited data
                  if (groupImages.length > 0) {
                    return getOrCreateBlobUrl(groupImages[0]);
                  }
                  // Only trust materialPath if isMultiple was explicitly saved as false
                  if (variant.visualization?.surface?.isMultiple === false && materialUrl) {
                    return materialUrl;
                  }
                  return undefined;
                }
                // Multiple mode: show the generated seamless texture
                if (materialUrl) {
                  return materialUrl;
                }
                // Fall back to thumbnailPath
                if (thumbnailUrl) {
                  return thumbnailUrl;
                }
                return undefined;
              })();

              // Initialize from existing seamlessParams, then fallback to local state, then defaults
              // Note: zoom must be >= 1 for react-easy-crop to work (minZoom is 1)
              const savedCrop = variant.visualization?.surface?.seamlessParams?.crop;
              const savedZoom = variant.visualization?.surface?.seamlessParams?.zoom;
              const currentCrop = variantCrops[variant.uuid] || savedCrop || { x: 0, y: 0 };
              // Ensure zoom is at least 1 (minZoom) - a zoom of 0 would cause black screen
              const rawZoom = variantZooms[variant.uuid] ?? savedZoom ?? 1;
              const currentZoom = rawZoom < 1 ? 1 : rawZoom;

              return (
                <SortableVariantRows key={variant.uuid} id={variant.uuid} disabled={!variant.isSelected || !onReorderVariants}>
                  {({ dragHandleProps, isDragging }) => (
                  <>
                  {/* Variant Row */}
                  <tr
                    className={cn(
                      'group transition-colors',
                      '[&>td]:border-y [&>td:first-child]:border-s [&>td:last-child]:border-e',
                      variant.isSelected
                        ? '[&>td]:border-border'
                        : '[&>td]:border-destructive/20 opacity-50',
                      isDragging
                        ? 'cursor-grabbing bg-muted'
                        : cn(
                            variant.isSelected ? 'cursor-pointer bg-card' : 'cursor-default bg-destructive/5',
                            'hover:[&>td]:border-foreground/20'
                          )
                    )}
                    onClick={() => variant.isSelected && toggleVariant(variant.uuid, imageGroupKey, uploadMode === 'multiple')}
                  >
                    {/* Drag handle */}
                    {onReorderVariants && (
                      <td
                        className={cn(
                          cellClass,
                          'w-8 px-1',
                          isDragging ? 'cursor-grabbing' : variant.isSelected ? 'cursor-grab' : 'cursor-default'
                        )}
                        onClick={(e) => e.stopPropagation()}
                        {...(variant.isSelected ? dragHandleProps : {})}
                      >
                        <GripVertical
                          className={cn(
                            'size-4 text-muted-foreground transition-opacity',
                            isDragging ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'
                          )}
                        />
                      </td>
                    )}

                    {/* Expand/Collapse chevron */}
                    <td className={cn(cellClass, 'w-9 px-1')} onClick={(e) => e.stopPropagation()}>
                      <span
                        onClick={() => variant.isSelected && toggleVariant(variant.uuid, imageGroupKey, uploadMode === 'multiple')}
                        className={cn(
                          'flex size-7 items-center justify-center transition-colors',
                          variant.isSelected ? 'cursor-pointer hover:bg-muted' : 'cursor-default'
                        )}
                      >
                        <ChevronRight
                          className={cn(
                            'size-3.5 transition-transform duration-200',
                            isExpanded && 'rotate-90',
                            variant.isSelected ? 'opacity-60' : 'opacity-30'
                          )}
                        />
                      </span>
                    </td>

                    {/* Image thumbnail */}
                    <td className={cn(cellClass, 'w-14')} onClick={(e) => e.stopPropagation()}>
                      <div
                        className={cn(
                          'relative flex size-11 items-center justify-center overflow-hidden border bg-muted/40',
                          thumbnailUrl || texturePreviewUrl ? 'border-foreground/20' : 'border-border'
                        )}
                      >
                        {isGenerating ? (
                          <>
                            <Skeleton className="absolute inset-0 size-full" />
                            <Loader2 className="relative size-4 animate-spin text-muted-foreground" />
                          </>
                        ) : thumbnailUrl ? (
                          <ThumbnailWithSkeleton src={thumbnailUrl} alt={getVariantDisplayName(variant)} />
                        ) : (
                          <ImageIcon className="size-4.5 text-muted-foreground" strokeWidth={1.5} />
                        )}
                      </div>
                    </td>

                    {/* Variant axes + status indicators */}
                    <td className={cellClass}>
                      <div className="flex items-center gap-2">
                        {/* Axes pills */}
                        <div className="flex flex-wrap items-center gap-1">
                          {enabledAxes.map((axis) => {
                            const value = variant.axes[axis.name];
                            if (value === null || value === undefined) return null;
                            return (
                              <AxisPill
                                key={axis.name}
                                value={value}
                                label={translateAxisValue(axis.name, value)}
                              />
                            );
                          })}
                        </div>

                        {/* Status dots */}
                        {variant._isNew && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="size-1.5 shrink-0 rounded-full bg-success" />
                            </TooltipTrigger>
                            <TooltipContent>New variant</TooltipContent>
                          </Tooltip>
                        )}
                        {isVariantModified(variant) && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="size-1.5 shrink-0 rounded-full bg-warning" />
                            </TooltipTrigger>
                            <TooltipContent>Modified</TooltipContent>
                          </Tooltip>
                        )}

                        {/* Generating indicator */}
                        {isGenerating && (
                          <span className="ms-1 text-xs italic text-muted-foreground">Generating...</span>
                        )}
                      </div>
                    </td>

                    {/* SKU — muted */}
                    <td className={cn(cellClass, 'min-w-20')} onClick={(e) => e.stopPropagation()}>
                      <span
                        className={cn(
                          'text-xs tracking-wide',
                          variant.sku?.trim() ? 'text-muted-foreground' : 'italic text-muted-foreground/70',
                          !variant.isSelected && 'opacity-50'
                        )}
                      >
                        {variant.sku?.trim() || 'No SKU'}
                      </span>
                    </td>

                    {/* Default button */}
                    <td className={cn(cellClass, 'w-[130px] text-center')} onClick={(e) => e.stopPropagation()}>
                      {variant.isDefault ? (
                        <span className="inline-flex items-center gap-1 border border-warning/25 bg-warning/10 px-2 py-0.5 text-xs font-medium whitespace-nowrap text-warning">
                          <span className="size-1.5 shrink-0 rounded-full bg-warning" />
                          {t('PAGES.NEW_PRODUCT.V3.DEFAULT_VARIANT')}
                        </span>
                      ) : variant.isSelected ? (
                        <Button
                          variant="ghost"
                          size="xs"
                          className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
                          onClick={() => onSetDefault(variant.uuid)}
                        >
                          {t('PAGES.NEW_PRODUCT.V3.SET_DEFAULT')}
                        </Button>
                      ) : null}
                    </td>

                    {/* Delete/Restore button */}
                    <td className={cn(cellClass, 'w-10 pe-1.5 text-center')} onClick={(e) => e.stopPropagation()}>
                      {variant.isSelected ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Delete variant"
                              className="text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => {
                                if (variant.isDefault) {
                                  const otherSelected = variants.filter(
                                    (v) => v.isSelected && v.uuid !== variant.uuid
                                  );
                                  if (otherSelected.length > 0) {
                                    setPendingDefaultDeletion(variant.uuid);
                                    setSelectedNewDefault(null);
                                    return;
                                  }
                                }
                                onDeleteVariant(variant.uuid);
                              }}
                            >
                              <Trash2 />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Delete variant</TooltipContent>
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Restore variant"
                              className="text-destructive hover:bg-success/10 hover:text-success"
                              onClick={() => onSelectVariant(variant.uuid, true)}
                            >
                              <CircleArrowUp />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">Restore variant</TooltipContent>
                        </Tooltip>
                      )}
                    </td>
                  </tr>

                  {/* Expanded content */}
                  <tr>
                    <td colSpan={onReorderVariants ? 7 : 6} className="p-0">
                      {isExpanded && variant.isSelected && (
                        <div className="mb-0.5 border border-border bg-card rounded-xl p-6">
                          {/* Variant details — SKU + Upload Mode */}
                          <div className="mb-5 flex items-center gap-5">
                            <div className="flex items-center gap-2.5">
                              <Label htmlFor={`variant-${variant.uuid}-sku`} className="shrink-0 whitespace-nowrap text-xs font-medium text-muted-foreground">
                                SKU *
                              </Label>
                              <Input
                                id={`variant-${variant.uuid}-sku`}
                                value={variant.sku || ''}
                                onChange={(e) => onVariantChange(variant.uuid, 'sku', e.target.value)}
                                placeholder="e.g., PROD-001-BLK"
                                aria-invalid={!variant.sku?.trim()}
                                onClick={(e) => e.stopPropagation()}
                                className="w-[180px]"
                              />
                            </div>
                            {isSurfaceProduct && (
                              <div className="ms-auto" onClick={(e) => e.stopPropagation()}>
                                <div className="inline-flex gap-px border border-border bg-muted p-px">
                                  {/* Multiple tiles option */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (uploadMode === 'multiple') return;
                                      setUploadMode(variant, 'multiple');
                                    }}
                                    aria-pressed={uploadMode === 'multiple'}
                                    className={cn(
                                      'flex items-center gap-1.5 px-3.5 py-1.5 text-xs whitespace-nowrap transition-colors',
                                      uploadMode === 'multiple'
                                        ? 'bg-card font-medium text-foreground'
                                        : 'text-muted-foreground hover:bg-card/60'
                                    )}
                                  >
                                    <Images className="size-4" />
                                    {t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_MULTIPLE')}
                                  </button>
                                  {/* Single texture option */}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (uploadMode === 'single') return;
                                      setUploadMode(variant, 'single');
                                    }}
                                    aria-pressed={uploadMode === 'single'}
                                    className={cn(
                                      'flex items-center gap-1.5 px-3.5 py-1.5 text-xs whitespace-nowrap transition-colors',
                                      uploadMode === 'single'
                                        ? 'bg-card font-medium text-foreground'
                                        : 'text-muted-foreground hover:bg-card/60'
                                    )}
                                  >
                                    <Crop className="size-4" />
                                    {t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_SINGLE')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Surface product content */}
                          {isSurfaceProduct && (
                            <SurfaceExpandedContent
                              variant={variant}
                              uploadMode={uploadMode}
                              plankGroupId={imageGroupKey}
                              groupImages={groupImages}
                              loadingPlankGroups={loadingPlankGroups}
                              onPlankImagesChange={onPlankImagesChange}
                              onUploadModeChange={(mode) => setUploadMode(variant, mode)}
                              productPhotoUrls={productPhotoUrls(variant)}
                              isGenerating={isGenerating}
                              onGenerateMaterial={onGenerateMaterial}
                              randomize={randomize}
                              setRandomize={setRandomize}
                              mirror={mirror}
                              setMirror={setMirror}
                              texturePreviewUrl={texturePreviewUrl}
                              currentCrop={currentCrop}
                              currentZoom={currentZoom}
                              onVariantCropChange={onVariantCropChange}
                              onVariantZoomChange={onVariantZoomChange}
                              setVariantCrops={setVariantCrops}
                              setVariantZooms={setVariantZooms}
                              setVariantCroppedAreas={setVariantCroppedAreas}
                              variantCropsRef={variantCropsRef}
                              variantZoomsRef={variantZoomsRef}
                              cropperInteractedRef={cropperInteractedRef}
                              settingThumbnail={settingThumbnail}
                              autoSaveThumbnail={autoSaveThumbnail}
                              onVariantGroutSizeChange={onVariantGroutSizeChange}
                              onVariantGroutColorChange={onVariantGroutColorChange}
                              onSeamlessWidthChange={onSeamlessWidthChange}
                              onSeamlessHeightChange={onSeamlessHeightChange}
                              onVariantThumbnailChange={onVariantThumbnailChange}
                              onVariantLinkChange={onVariantLinkChange}
                              onVariantAddToCartLinkChange={onVariantAddToCartLinkChange}
                            />
                          )}

                          {/* 3D Model product content */}
                          {is3DProduct && (
                            <Model3DExpandedContent
                              variant={variant}
                              thumbnailUrl={thumbnailUrl}
                              onVariantImageChange={onVariantImageChange}
                              modelUrl={modelUrl}
                              onVariantModelChange={onVariantModelChange}
                              getPendingModelFile={getPendingModelFile}
                              onGenerate3DModel={onGenerate3DModel}
                              generating3DModels={generating3DModels}
                              usdzUrl={usdzUrl}
                              onVariantUsdzChange={onVariantUsdzChange}
                              onVariantLinkChange={onVariantLinkChange}
                              onVariantAddToCartLinkChange={onVariantAddToCartLinkChange}
                            />
                          )}

                          {/* Image-only or other types: Just links */}
                          {!isSurfaceProduct && !is3DProduct && (
                            <ImageOnlyExpandedContent
                              variant={variant}
                              onVariantLinkChange={onVariantLinkChange}
                              onVariantAddToCartLinkChange={onVariantAddToCartLinkChange}
                            />
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                </>
                  )}
                </SortableVariantRows>
              );
            })}
            </SortableContext>
          </DndContext>
        </table>

        {/* Upload Mode Info Dialog */}
        <Dialog open={showUploadModeInfo} onOpenChange={(open) => !open && setShowUploadModeInfo(false)}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-center">
                {t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODES_TITLE')}
              </DialogTitle>
              <DialogDescription>
                {t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODES_DESCRIPTION')}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3">
              {/* Multiple Planks card */}
              <div className="flex min-h-[220px] flex-col items-center border border-border p-4 text-center">
                <Images className="mb-2 size-7 text-muted-foreground" strokeWidth={1.5} />
                <p className="mb-3 text-xs font-medium text-foreground">
                  {t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_MULTIPLE')}
                </p>
                <ul className="w-full list-disc space-y-1 ps-4 text-start text-xs/relaxed text-muted-foreground">
                  <li>{t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_MULTIPLE_FEATURE_1')}</li>
                  <li>{t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_MULTIPLE_FEATURE_2')}</li>
                  <li>{t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_MULTIPLE_FEATURE_3')}</li>
                  <li>{t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_MULTIPLE_FEATURE_4')}</li>
                </ul>
                <p className="mt-auto pt-3 text-xs font-medium text-muted-foreground">
                  {t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_RECOMMENDED', { defaultValue: 'Recommended' })}
                </p>
              </div>

              {/* Single Texture card */}
              <div className="flex min-h-[220px] flex-col items-center border border-border p-4 text-center">
                <Crop className="mb-2 size-7 text-muted-foreground" strokeWidth={1.5} />
                <p className="mb-3 text-xs font-medium text-foreground">
                  {t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_SINGLE')}
                </p>
                <ul className="w-full list-disc space-y-1 ps-4 text-start text-xs/relaxed text-muted-foreground">
                  <li>{t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_SINGLE_FEATURE_1')}</li>
                  <li>{t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_SINGLE_FEATURE_2')}</li>
                  <li>{t('PAGES.NEW_PRODUCT.V3.UPLOAD_MODE_SINGLE_FEATURE_3')}</li>
                </ul>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowUploadModeInfo(false)}>
                {t('COMMON.GOT_IT')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Select New Default Dialog */}
        <Dialog
          open={pendingDefaultDeletion !== null}
          onOpenChange={(open) => {
            if (!open) {
              setPendingDefaultDeletion(null);
              setSelectedNewDefault(null);
            }
          }}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                {t('PAGES.NEW_PRODUCT.V3.SELECT_NEW_DEFAULT', { defaultValue: 'Select a new default variant' })}
              </DialogTitle>
              <DialogDescription>
                {t('PAGES.NEW_PRODUCT.V3.SELECT_NEW_DEFAULT_MESSAGE', { defaultValue: "You're deleting the default variant. Please select which variant should become the new default." })}
              </DialogDescription>
            </DialogHeader>

            <RadioGroup
              value={selectedNewDefault || ''}
              onValueChange={(value) => setSelectedNewDefault(value)}
            >
              {variants
                .filter((v) => v.isSelected && v.uuid !== pendingDefaultDeletion)
                .map((v) => {
                  const thumbUrl = getImageSrc(v.visualization?.thumbnailPath);
                  return (
                    // A row, not a <label>: a <label> does not forward its click to
                    // the Radix radio (a <button>), so the whole row handles it.
                    <div
                      key={v.uuid}
                      onClick={() => setSelectedNewDefault(v.uuid)}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 px-2 py-1 transition-colors hover:bg-muted',
                        selectedNewDefault === v.uuid && 'bg-muted'
                      )}
                    >
                      <RadioGroupItem value={v.uuid} />
                      <span className="relative flex size-9 shrink-0 items-center justify-center overflow-hidden border border-border bg-muted">
                        {thumbUrl ? (
                          <ThumbnailWithSkeleton src={thumbUrl} alt={getVariantDisplayName(v)} />
                        ) : (
                          <ImageIcon className="size-4.5 text-muted-foreground" strokeWidth={1.5} />
                        )}
                      </span>
                      <span className="flex flex-wrap items-center gap-1">
                        {enabledAxes.map((axis) => {
                          const axisVal = v.axes[axis.name];
                          if (axisVal === null || axisVal === undefined) return null;
                          return (
                            <AxisPill
                              key={axis.name}
                              value={axisVal}
                              label={translateAxisValue(axis.name, axisVal)}
                            />
                          );
                        })}
                      </span>
                    </div>
                  );
                })}
            </RadioGroup>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setPendingDefaultDeletion(null);
                  setSelectedNewDefault(null);
                }}
              >
                {t('COMMON.CANCEL')}
              </Button>
              {/* Outlined, not filled: the destructive path is never the loudest button. */}
              <Button
                variant="outline"
                className="border-destructive/40 text-destructive hover:bg-destructive/5 hover:text-destructive"
                onClick={() => {
                  if (selectedNewDefault && pendingDefaultDeletion) {
                    onSetDefault(selectedNewDefault);
                    onDeleteVariant(pendingDefaultDeletion);
                  }
                  setPendingDefaultDeletion(null);
                  setSelectedNewDefault(null);
                }}
                disabled={!selectedNewDefault}
              >
                {t('COMMON.CONFIRM')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
};

export default VariantMatrix;
